import { observer } from "mobx-react";
import {
  autofocusHeading,
  autofocusText,
  CustomDocumentForm,
  CustomDocumentFreetextBlockForm,
  CustomDocumentPersonalDrawdownContributionBlockForm,
  CustomDocumentShareholdersBlockForm,
  CustomDocumentSignatoryForm,
  CustomDocumentTransactionLogBlockForm,
  getCustomDocumentBlockPanels,
  getCustomDocumentHeaderPanels,
  getCustomDocumentSettingsPanels,
  getCustomDocumentSignaturePanels,
  prepAddBlockDropdown,
  prepAddSenderItems,
  prepAddSignatoryItems,
  batchQuickAdd,
  batchQuickAddTooltip,
  hasAnyRecipient,
  quickAdd,
  removeAllRecipients,
  showAutoTranslateOptions,
  signatoryOptionLabel,
  RecipientsDialogState,
} from "./customDocumentDomain";
import { Box, VBox } from "@/components-v2/Box";
import {
  DetailsSummaryList,
  DocumentLanguageSelector,
  DocumentSigningStatusLine,
  DocumentViewingStatusLine,
  ReopenDocumentButton,
} from "@/dbot/common/commonComponents";
import {
  DetailsPanel,
  DynamicRecipientType,
  isDocumentSent,
  ProjectCompany,
} from "@/dbot/common/commonDomain";
import { useTranslation } from "@/stores/RootStoreContext";
import { constant, identity, set } from "lodash";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { MDateInput } from "@/components-v2/DateInput";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import { MRadioButtonGroup, RadioButtonGroup } from "@/components-v2/RadioButton";
import { MSelect } from "@/components-v2/Select";
import { MTextInput } from "@/components-v2/TextInput";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { Alert } from "@/components-v2/Alert";
import { SelectFilesButton, SelectFilesInput } from "@/components-v2/SelectFilesButton";
import {
  AttachmentListItem,
  AttachmentsPanel,
  UploadingAttachmentListItem,
} from "@/components-v2/AttachmentsPanel";
import { when } from "@/utils/functions";
import {
  prepDocumentAttachmentsPanel,
  prepUploadDocumentAttachmentsButton,
} from "@/dbot/attachments";
import { Sortable, SortableList, SortableListProps } from "@/components-v2/SortableList";
import { Icon } from "@/components-v2/Icon";
import { DynamicRecipientOption, RecipientsEditModel } from "@/dbot/summary/summaryDomain";
import { Details } from "@/components-v2/Details";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { isPending } from "@/common/common";
import { Markdown } from "@/text/Markdown";
import { DocumentRecipients, SendDocumentForViewingDialog } from "@/common/commonComponents";
import { ReactNode } from "react";
import { action } from "mobx";
import { Dialog } from "@/components-v2/Dialog.tsx";

export const CustomDocumentView = observer(({ form }: { form: CustomDocumentForm }) => {
  const { t } = useTranslation();
  const attachmentsPanel = prepDocumentAttachmentsPanel(form.context, form.dispatch);
  const uploadAttachmentsButton = prepUploadDocumentAttachmentsButton(form.context, form.dispatch);
  const configPanelsLocked = form.context.isLocked || form.isPanelsLocked();

  return (
    <VBox gap={form.context.isLocked ? "xs" : "md"}>
      {form.isSentForSigning ? (
        <DocumentSigningStatusLine context={form.context} dispatch={form.dispatch} />
      ) : form.isSentForViewing ? (
        <DocumentViewingStatusLine context={form.context} dispatch={form.dispatch} />
      ) : undefined}
      <DetailsSummaryList
        dispatch={form.dispatch}
        isLocked={configPanelsLocked}
        panels={getCustomDocumentSettingsPanels(form)}
      />
      {form.headerEnabled && (
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={configPanelsLocked}
          panels={getCustomDocumentHeaderPanels(form)}
        />
      )}
      <DetailsSummaryList
        dispatch={form.dispatch}
        isLocked={form.context.isLocked}
        panels={getCustomDocumentBlockPanels(form)}
        canMove={constant(true)}
        onMove={form.moveBlock}
      />
      {when(prepAddBlockDropdown(form, uploadAttachmentsButton), (props) => (
        <DropdownMenu {...props} />
      ))}
      {form.signatureEnabled && (
        <DetailsSummaryList
          dispatch={form.dispatch}
          isLocked={configPanelsLocked}
          panels={getCustomDocumentSignaturePanels(form)}
        />
      )}
      {attachmentsPanel && (
        <AttachmentsPanel {...attachmentsPanel} className="mt-md empty:hidden" />
      )}
      {uploadAttachmentsButton && <SelectFilesInput {...uploadAttachmentsButton} />}
      {form.showsBusinessInformationInfo && (
        <Alert
          icon
          type="info"
          message={{ key: "dbot.custom-document.business-information.info" }}
        />
      )}
      <VBox gap={"md"} marginTop={!form.showsBusinessInformationInfo}>
        {when(form.downloadSignedDocumentAction, (action) => (
          <Button
            variant={"secondary"}
            label={action.label}
            onClick={() => form.dispatch(action)}
          />
        ))}
        <ReopenDocumentButton context={form.context} dispatch={form.dispatch} />
        {!isDocumentSent(form.context, form.context.documentId) && form.hasRecipients ? (
          <>
            {form.canSendDocument() && !form.requireSignature && (
              <Button
                fullWidth
                variant={form.requireSignature ? "secondary" : "primary"}
                label={t("dbot.send-document")}
                loading={isPending(form.context.documentSigners.get(form.context.documentId))}
                onClick={() => form.openSendCustomDocumentDialog()}
                splitActions={
                  form.openSendForSigningDialogAction
                    ? [
                        {
                          type: "item",
                          icon: "paper-plane",
                          label: t("dbot.send-document"),
                          onSelect: () => form.openSendCustomDocumentDialog(),
                        },
                        {
                          type: "item",
                          icon: "pen",
                          label: form.openSendForSigningDialogAction.label,
                          onSelect: () => form.dispatch(form.openSendForSigningDialogAction!),
                        },
                      ]
                    : undefined
                }
              />
            )}
            {form.requireSignature && form.openSendForSigningDialogAction && (
              <>
                <Button
                  fullWidth
                  variant={"primary"}
                  label={form.openSendForSigningDialogAction.label}
                  loading={isPending(form.context.documentSigners.get(form.context.documentId))}
                  disabled={form.awaitsIncorporationDocumentSealing}
                  onClick={() => form.dispatch(form.openSendForSigningDialogAction!)}
                  splitActions={
                    !form.awaitsIncorporationDocumentSealing && form.canSendDocument()
                      ? [
                          {
                            type: "item",
                            icon: "pen",
                            label: form.openSendForSigningDialogAction.label,
                            onSelect: () => form.dispatch(form.openSendForSigningDialogAction!),
                          },
                          {
                            type: "item",
                            icon: "paper-plane",
                            label: t("dbot.send-document"),
                            onSelect: () => form.openSendCustomDocumentDialog(),
                          },
                        ]
                      : undefined
                  }
                />
                {when(form.awaitingIncorporationDocumentSealingInfo, (info) => (
                  <Box fg={"subtle"} textSize={"sm"} textAlign={"center"}>
                    {t(info)}
                  </Box>
                ))}
              </>
            )}
          </>
        ) : form.hasNewRecipients && (form.personalised || !form.isSentForSigning) ? (
          <>
            <Alert
              icon
              type="warning"
              message={{
                key: "dbot.custom-document.send-to-new-recipients.info",
                params: { count: form.newRecipientsCount },
              }}
            />
            <Button
              variant={"primary"}
              label={{
                key: "dbot.custom-document.send-to-new-recipients",
                params: { count: form.newRecipientsCount },
              }}
              onClick={form.openSendToNewRecipientsDialog}
            />
          </>
        ) : !form.isSentForSigning && !form.isSentForViewing ? (
          <VBox textAlign="center" alignItems="center">
            <Icon name="lightbulb" />
            <Markdown
              value={t("dbot.custom-document.add-recipients-tip")}
              onActionClick={form.openRecipientsPanel}
              enableLinks
            />
          </VBox>
        ) : undefined}
      </VBox>
      {form.sendDocumentForViewingModel && (
        <SendDocumentForViewingDialog model={form.sendDocumentForViewingModel} />
      )}
    </VBox>
  );
});

export const CustomDocumentDetailsPanel = observer(
  ({ panel, form }: { panel: DetailsPanel; form: CustomDocumentForm }) => {
    switch (panel.id) {
      case "document-settings":
        return <CustomDocumentSettings form={form} />;
      case "recipients":
        return <CustomDocumentRecipientsPanel form={form} />;
      case "signature":
        return <CustomDocumentSignaturesPanel form={form} />;
      case "header":
        return <CustomDocumentHeaderPanel form={form} />;
      case "blob-header":
        return <CustomDocumentBlobHeaderPanel form={form} />;

      default: {
        const block = form.blocks.find((b) => b.id === panel.id);
        switch (block?.type) {
          case "freetext":
            return <CustomDocumentFreetextPanel form={form} block={block} />;
          case "shareholders":
            return <CustomDocumentShareholdersPanel form={form} block={block} />;
          case "subscribers":
            return <CustomDocumentSubscribersPanel />;
          //return <CustomDocumentSubscribersPanel form={form} block={block} />;
          case "transactionLog":
            return <CustomDocumentTransactionLogPanel form={form} block={block} />;
          case "personalDrawdownContribution":
            return <CustomDocumentPersonalDrawdownContributionPanel form={form} block={block} />;
          default:
            return null;
        }
      }
    }
  },
);

const CustomDocumentSettings = observer(({ form }: { form: CustomDocumentForm }) => {
  const { t } = useTranslation();
  const isLocked = form.context.isLocked || form.isPanelsLocked();
  return (
    <VBox gap="lg" paddingLeft={"sm"}>
      {form.customDocumentType !== "blob" && (
        <DocumentLanguageSelector field={form.documentLanguage} isLocked={isLocked} />
      )}
      <LocalizedTextEditor
        model={form.title}
        useV2AutotranslateOption
        inputType={"string"}
        autoselect
      />
      {form.customDocumentTypeHint && (
        <Alert
          type="info"
          message={form.customDocumentTypeHint}
          onClose={form.clearCustomDocumentTypeHint}
        />
      )}
      {t("options")}
      <Checkbox
        label={t("dbot.custom-document.require-signature")}
        descriptionText={t("dbot.custom-document.require-signature.description")}
        checked={form.requireSignature}
        onChange={() => form.toggleRequireSignature()}
        disabled={isLocked}
      />
      {form.customDocumentType !== "blob" && (
        <Checkbox
          label={t("dbot.custom-document.personalised")}
          descriptionText={t("dbot.custom-document.personalised.description")}
          checked={form.personalised}
          onChange={() => form.togglePersonalised()}
          disabled={isLocked}
        />
      )}
    </VBox>
  );
});

const CustomDocumentRecipientsPanel = observer(({ form }: { form: CustomDocumentForm }) => {
  const { t } = useTranslation();
  const companies = form.context.companies;
  const render = (orgNo: string, recipients: RecipientsEditModel | undefined) => {
    const quickAddItems = quickAdd(form, orgNo);
    const dynamicRecipients: DynamicRecipientOption[] | undefined = quickAddItems
      .filter((item) => item.isDynamic)
      .map((d) => ({
        type: d.name as DynamicRecipientType,
        count: d.count ?? 0,
      }));
    const isReadOnly = (form.isSentForSigning && !form.personalised) || form.isPanelsLocked();
    const sentRecipientIds = new Set(
      form.documentStatus?.viewerRecipientIds?.map((id) => String(id)) ?? [],
    );
    return !recipients ? (
      <LoadingIndicator />
    ) : (
      <>
        {!form.isValidForCompany(orgNo) && (
          <Alert message={t("recipients-model.validation.recipients-missing")} type={"warning"} />
        )}
        <DocumentRecipients
          recipients={recipients}
          alert={form.recipientsAlert}
          dynamicRecipients={dynamicRecipients}
          isReadOnly={isReadOnly}
          sentRecipientIds={sentRecipientIds}
        />
        <LinkButton
          className="self-start"
          icon={"plus"}
          label={{ key: "add-recipient" }}
          onClick={() => form.addNewRecipient(orgNo)}
          disabled={isReadOnly}
        />
        <Box wrap>
          {!isReadOnly &&
            quickAddItems.map((item) => (
              <Button
                key={item.name}
                variant={"basic"}
                disabled={item.disabled}
                label={
                  item.count !== undefined ? `${t(item.label)} (${item.count})` : t(item.label)
                }
                icon={"plus"}
                onClick={() => form.quickAddRecipients(orgNo, item)}
                size={"sm"}
                tooltip={item.tooltip}
              />
            ))}
        </Box>
        {form.recipientsDialog?.orgNo === orgNo && (
          <RecipientsDialog form={form} dialog={form.recipientsDialog} />
        )}
      </>
    );
  };

  if (companies.length === 1) {
    return (
      <VBox gap={"lg"}>
        {form.requireSignature && <strong>{t("dbot.custom-document.recipients.who-signs")}</strong>}
        {render(companies[0].orgNo, form.getRecipientsEditModel(companies[0].orgNo))}
      </VBox>
    );
  }
  return (
    <VBox gap={false}>
      {form.requireSignature && <strong>{t("dbot.custom-document.recipients.who-signs")}</strong>}
      <BatchQuickAddRow form={form} />
      <Box marginTop={"lg"}>{t("dbot.custom-document.recipients.per-company")}</Box>
      {companies.map((c) => {
        const recipients = form.getRecipientsEditModel(c.orgNo);
        return (
          <Details
            key={c.orgNo}
            title={
              <Box>
                {`${c.name} (${recipients?.count ?? 0})`}
                {!form.isValidForCompany(c.orgNo) && (
                  <Icon name={"warning"} color={"error"} className={"align-text-bottom"} />
                )}
              </Box>
            }
            open={form.selectedCompany === c.orgNo}
            onToggle={(open) => form.toggleCompany(c.orgNo, open)}
            bg={false}
            borderTop
            caretPosition="left"
            titleBoxProps={{ paddingX: false }}
            content={
              <VBox gap={"lg"} marginY={"lg"}>
                {render(c.orgNo, recipients)}
              </VBox>
            }
          />
        );
      })}
    </VBox>
  );
});

const BatchQuickAddRow = observer(({ form }: { form: CustomDocumentForm }) => {
  const { t } = useTranslation();
  if (form.isPanelsLocked()) {
    return null;
  }
  return (
    <Box justifyContent={"space-between"} alignItems={"center"} marginY={"md"}>
      <Box wrap>
        {batchQuickAdd(form).map((item) => (
          <Button
            key={item.name}
            variant={"basic"}
            size={"sm"}
            icon={"plus"}
            disabled={item.recipientsByCompany.length === 0}
            label={t(item.label)}
            onClick={() =>
              item.recipientsByCompany.forEach(({ orgNo, recipient }) =>
                form.addRecipients(orgNo, [recipient]),
              )
            }
            tooltip={batchQuickAddTooltip(item, form.requireSignature, t)}
          />
        ))}
      </Box>
      <LinkButton
        icon={"arrow-counter-clockwise"}
        label={t("remove-all")}
        onClick={() => removeAllRecipients(form)}
        disabled={!hasAnyRecipient(form)}
      />
    </Box>
  );
});

const CustomDocumentSignatoryList = observer(
  ({
    items,
    onMove,
    onRemove,
    isLocked,
  }: {
    items: CustomDocumentSignatoryForm[];
    onMove: SortableListProps<CustomDocumentSignatoryForm>["onMove"];
    onRemove: (item: CustomDocumentSignatoryForm) => void;
    isLocked?: boolean;
  }) => {
    const { t } = useTranslation();

    function render(form: CustomDocumentSignatoryForm) {
      switch (form.option) {
        case "freetext":
          return (
            <Box>
              <MTextInput model={form.freetextName} placeholder={t("name")} disabled={isLocked} />
              <MTextInput
                model={form.freetextRole.textModels[0].model}
                placeholder={t("role")}
                disabled={isLocked}
              />
            </Box>
          );
        default:
          return <Box>{t(signatoryOptionLabel[form.option])}</Box>;
      }
    }
    return (
      <SortableList items={items} onMove={onMove}>
        {items.map((s) => (
          <Sortable key={s.id} id={s.id} isDragDisabled={isLocked}>
            {(props) => (
              <Box alignItems="center">
                <Box htmlProps={{ ...props.dragAttributes, ...props.dragListeners }}>
                  <Icon name={"dots-six-vertical"} />
                </Box>
                <Box grow>{render(s)}</Box>
                {!isLocked && (
                  <IconButton
                    tooltip={{ key: "remove" }}
                    icon={"trash"}
                    color={"error"}
                    onClick={() => onRemove(s)}
                  />
                )}
              </Box>
            )}
          </Sortable>
        ))}
      </SortableList>
    );
  },
);

const CustomDocumentSignaturesPanel = observer(({ form }: { form: CustomDocumentForm }) => {
  const { t } = useTranslation();
  const isLocked =
    form.context.isLocked ||
    form.isPanelsLocked() ||
    form.signaturesChangeMode.value === "automatic";
  return (
    <VBox gap={"xl"}>
      <VBox gap={"md"}>
        <MRadioButtonGroup
          model={form.signaturesChangeMode}
          horizontal
          disabled={form.context.isLocked || form.isPanelsLocked()}
        />
        <strong>{t("dbot.custom-document.sender")}</strong>
        <LocalizedTextEditor
          model={form.signatureSendersStatement}
          placeholder={{ key: "dbot.custom-document.senders-statement.placeholder" }}
          useV2AutotranslateOption={showAutoTranslateOptions(form)}
        />
        <VBox>
          <CustomDocumentSignatoryList
            items={form.signatureSenders}
            onMove={(...args) => form.moveSignatory("signatureSenders", ...args)}
            onRemove={(...args) => form.removeSignatory("signatureSenders", ...args)}
            isLocked={isLocked}
          />
        </VBox>
        <div>
          {!isLocked && (
            <DropdownMenu
              button={{
                type: "link",
                icon: "plus",
                label: { key: "dbot.custom-document.add-sender" },
              }}
              items={prepAddSenderItems(form)}
            />
          )}
        </div>
      </VBox>
      <VBox gap={"md"}>
        <strong>{t("dbot.custom-document.signatories")}</strong>
        <LocalizedTextEditor
          model={form.signatoriesStatement}
          placeholder={{ key: "dbot.custom-document.signatories-statement.placeholder" }}
          useV2AutotranslateOption={showAutoTranslateOptions(form)}
        />
        <VBox>
          <CustomDocumentSignatoryList
            items={form.signatories}
            onMove={(...args) => form.moveSignatory("signatories", ...args)}
            onRemove={(...args) => form.removeSignatory("signatories", ...args)}
            isLocked={isLocked}
          />
        </VBox>
        <div>
          {!isLocked && (
            <DropdownMenu
              button={{
                type: "link",
                icon: "plus",
                label: { key: "dbot.custom-document.add-signatory" },
              }}
              items={prepAddSignatoryItems(form)}
            />
          )}
        </div>
      </VBox>
    </VBox>
  );
});

const CustomDocumentBlobHeaderPanel = observer(({ form }: { form: CustomDocumentForm }) => {
  const { t } = useTranslation();

  function filenameForOrgNo(c: ProjectCompany): ReactNode {
    const filename = form.filenames ? form.filenames[c.orgNo] : undefined;
    if (filename) {
      return (
        <AttachmentListItem
          attachment={{
            filename,
            id: filename,
          }}
          onOpen={() => form.openCompany(c.orgNo)}
          onDelete={form.context.isLocked ? undefined : () => form.deleteDocumentBlob(c.orgNo)}
          isDeleting={form.blobStatus === "deleting"}
        />
      );
    }
    const uploading = form.uploadingBlobState(c.orgNo);
    if (uploading) {
      return (
        <UploadingAttachmentListItem
          upload={{
            id: c.orgNo,
            filename: uploading.filename,
            progress: uploading.progress,
            reference: form.documentItem.documentId,
            isConverting: uploading.isConverting,
          }}
        />
      );
    }
    return (
      <VBox>
        {t("not-set")}
        <div>
          <SelectFilesButton
            fileUploadConfig={form.context.fileUploadConfig}
            onChange={(files) => form.uploadDocumentBlob(files, c.orgNo)}
            multiple={false}
          />
        </div>
      </VBox>
    );
  }
  return (
    <VBox gap={"xl"}>
      {form.context.companies.length === 1
        ? filenameForOrgNo(form.context.companies[0])
        : form.context.companies.map((c) => (
            <VBox key={c.orgNo} gap>
              <div>{c.name}</div>
              {filenameForOrgNo(c)}
            </VBox>
          ))}
    </VBox>
  );
});

const CustomDocumentHeaderPanel = observer(({ form }: { form: CustomDocumentForm }) => {
  const isLocked = form.context.isLocked || form.isPanelsLocked();
  return (
    <VBox gap={"lg"}>
      <MRadioButtonGroup model={form.headerChangeMode} horizontal disabled={isLocked} />
      <Checkbox
        label={{ key: "dbot.custom-document.logo" }}
        checked={form.headerLogoEnabled}
        onChange={(event) => form.set("headerLogoEnabled", event.target.checked)}
        disabled={isLocked || form.headerChangeMode.value === "automatic"}
      />
      <Checkbox
        label={{ key: "dbot.custom-document.company-name" }}
        checked={form.headerCompanyNameEnabled}
        onChange={(event) => form.set("headerCompanyNameEnabled", event.target.checked)}
        disabled={isLocked || form.headerChangeMode.value === "automatic"}
      />
      <VBox>
        <Checkbox
          label={{ key: "dbot.custom-document.inside-address" }}
          disabled={isLocked || form.headerChangeMode.value === "automatic"}
          checked={form.insideAddressEnabled}
          onChange={(e) => form.setInsideAddressEnabled(e.target.checked)}
        />
        <MSelect
          model={form.insideAddressOption}
          disabled={
            isLocked || !form.insideAddressEnabled || form.headerChangeMode.value === "automatic"
          }
        />
        {form.insideAddressOption.value === "freetext" && (
          <MTextInput
            placeholder={{ key: "name" }}
            model={form.insideAddressFreetext}
            disabled={
              isLocked || !form.insideAddressEnabled || form.headerChangeMode.value === "automatic"
            }
          />
        )}
      </VBox>
      <Checkbox
        label={{ key: "dbot.custom-document.document-title" }}
        checked={form.headerTitlesEnabled}
        onChange={(event) => form.set("headerTitlesEnabled", event.target.checked)}
        disabled={isLocked || form.headerChangeMode.value === "automatic"}
      />
      <VBox>
        <Checkbox
          label={{ key: "dbot.custom-document.document-date" }}
          checked={form.headerDateEnabled}
          onChange={(event) => form.set("headerDateEnabled", event.target.checked)}
          disabled={isLocked || form.headerChangeMode.value === "automatic"}
        />
        <MSelect
          model={form.documentDateOption}
          disabled={
            isLocked || !form.signatureEnabled || form.headerChangeMode.value === "automatic"
          }
        />
        {form.documentDateOption.value === "custom" && (
          <MDateInput
            model={form.customDocumentDate}
            disabled={
              isLocked || !form.signatureEnabled || form.headerChangeMode.value === "automatic"
            }
          />
        )}
      </VBox>
    </VBox>
  );
});

const CustomDocumentFreetextPanel = observer(
  ({ form, block }: { form: CustomDocumentForm; block: CustomDocumentFreetextBlockForm }) => {
    const showTranslationOptions = showAutoTranslateOptions(form);
    return (
      <VBox gap={"lg"}>
        <LocalizedTextEditor
          model={block.heading}
          inputType={"string"}
          placeholder={{ key: "optional" }}
          autofocus={autofocusHeading(block)}
          useV2AutotranslateOption={showTranslationOptions}
        />
        <LocalizedTextEditor
          model={block.text}
          autofocus={autofocusText(block)}
          useV2AutotranslateOption={showTranslationOptions}
          includeSlashCommand
        />
        <VBox gap={"sm"}>
          <Checkbox
            label={{ key: "dbot.custom-document.page-break-before" }}
            checked={block.pageBreakBefore}
            onChange={action((event) => set(block, "pageBreakBefore", event.target.checked))}
            disabled={form.context.isLocked}
          />
        </VBox>
      </VBox>
    );
  },
);

const CustomDocumentShareholdersPanel = observer(
  ({ block, form }: { form: CustomDocumentForm; block: CustomDocumentShareholdersBlockForm }) => {
    return (
      <VBox>
        <MRadioButtonGroup
          horizontal
          model={block.currentOrPerDate}
          disabled={form.context.isLocked}
        />
        {block.showDateInput && (
          <MDateInput model={block.perDate} autofocus disabled={form.context.isLocked} />
        )}
      </VBox>
    );
  },
);

const CustomDocumentSubscribersPanel = observer(() => {
  //({ form, block }: { form: CustomDocumentForm; block: CustomDocumentSubscribersBlockForm }) => {
  return <VBox></VBox>;
});

const CustomDocumentTransactionLogPanel = observer(
  ({ block, form }: { block: CustomDocumentTransactionLogBlockForm; form: CustomDocumentForm }) => {
    const { t } = useTranslation();
    return (
      <VBox gap={"lg"}>
        <LocalizedTextEditor
          model={block.heading}
          useV2AutotranslateOption
          inputType={"string"}
          placeholder={{ key: "optional" }}
        />
        <VBox>
          <Box fontWeight={"bold"}>{t("type")}</Box>
          <RadioButtonGroup
            name={"transactionLogIsPersonalised"}
            horizontal
            selected={block.isPersonalised.value ?? false}
            options={[false, true]}
            idFn={identity}
            labelFn={(v) =>
              v
                ? t("dbot.transactions.isPersonalised.true")
                : t("dbot.transactions.isPersonalised.false")
            }
            onChange={(_, option) => block.isPersonalised.setValue(option)}
          />
          {!form.personalised && block.isPersonalised.value === true && (
            <Alert
              type={"warning"}
              message={
                <Markdown
                  value={t("dbot.transactions.isPersonalised.warning")}
                  onActionClick={form.openSettingsPanel}
                  enableLinks
                />
              }
            />
          )}
        </VBox>
        <Box>
          <MDateInput
            model={block.from}
            label={t("from")}
            autofocus
            disabled={form.context.isLocked}
          />
          <MDateInput model={block.to} label={t("to")} autofocus disabled={form.context.isLocked} />
        </Box>
        <Box fg={"subtle"} textSize={"sm"}>
          {block.transactionTypes.options.length === 0
            ? t("dbot.transactions.info.noTransactionsTypes")
            : t("dbot.transactions.info.transactionsTypes")}
        </Box>
        <MCheckboxGroup model={block.transactionTypes} disabled={form.context.isLocked} />
      </VBox>
    );
  },
);

const CustomDocumentPersonalDrawdownContributionPanel = observer(
  ({
    block,
  }: {
    form: CustomDocumentForm;
    block: CustomDocumentPersonalDrawdownContributionBlockForm;
  }) => {
    return (
      <VBox>
        <Alert message={{ key: "dbot.drawdown.your-contribution.info" }} type={"info"} />
        <LocalizedTextEditor
          model={block.heading}
          useV2AutotranslateOption
          inputType={"string"}
          placeholder={{ key: "optional" }}
        />
      </VBox>
    );
  },
);

const RecipientsDialog = observer(
  ({ form, dialog }: { form: CustomDocumentForm; dialog: RecipientsDialogState }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={{ key: "select", params: { name: dialog.item.label } }}
        onCancel={form.closeRecipientsDialog}
        onSubmit={form.addSelectedRecipients}
        submitLabel={{
          key: "dbot.recipients.add-selected",
          params: { count: dialog.selectedIds.length },
        }}
        isSubmitDisabled={dialog.selectedIds.length === 0}
        maxWidth={"sm"}
      >
        <VBox gap={"md"}>
          <MTextInput
            model={dialog.search}
            placeholder={{ key: "search" }}
            icon={"magnifying-glass"}
            type={"search"}
          />
          <Box gap={"md"}>
            <LinkButton label={t("select-all")} onClick={form.selectAllRecipients} />
            <LinkButton label={t("select-none")} onClick={form.deselectAllRecipients} />
          </Box>
          {form.visibleRecipients(dialog).map((r) => (
            <Box key={r.id} justifyContent={"space-between"}>
              <Checkbox
                label={r.name}
                checked={dialog.selectedIds.includes(r.id)}
                onChange={() => form.toggleRecipient(r.id)}
              />
              <div className={r.email ? undefined : "subtle"}>{r.email || t("not-set")}</div>
            </Box>
          ))}
        </VBox>
      </Dialog>
    );
  },
);
